<div class="flex flex-col bg-transparent rounded-2xl h-full" data-testid="dashboard-campaigns">
  <div class="flex items-center justify-between mb-4">
    <div class="font-medium">{{ selectedDateLabel() }}</div>

    <div class="flex items-center gap-2">
      <button mat-icon-button [disabled]="!canScrollLeft()" (click)="onScrollLeft()">
        <app-icon src="icons/chevron-left.svg" alt="chevron-left" [size]="16" />
      </button>

      <button mat-icon-button [disabled]="!canScrollRight()" (click)="onScrollRight()">
        <app-icon src="icons/chevron-right.svg" alt="chevron-right" [size]="16" />
      </button>

      <button
        *hasPermission="EPermissions.campaignCreate"
        mat-flat-button
        class="mat-slim-button !pl-2 !pr-3"
        (click)="onCreateCampaignClick()"
      >
        <div class="flex items-center gap-2">
          <app-icon src="icons/plus.svg" alt="plus" [size]="16" />
          {{ 'dialogs.createCampaignDialog.createCampaign' | translate }}
        </div>
      </button>
    </div>
  </div>

  <div class="campaigns-scroll-container" #scrollContainer (scroll)="onScroll()">
    <div class="flex gap-4 pb-2">
      @for (campaign of loadingCampaigns() ? dummyCampaigns : filteredCampaigns(); track campaign.id) {
        <div class="min-w-[236px] max-w-[236px] rounded-2xl px-3 pt-2 pb-3 border border-shikenso-purple-base-12">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              @if (loadingCampaigns()) {
                <ngx-skeleton-loader
                  class="h-6"
                  [theme]="{ width: '48px', height: '24px', 'border-radius': '100vw' }"
                />
                <ngx-skeleton-loader class="h-4" [theme]="{ width: '80px', height: '16px', 'border-radius': '4px' }" />
              } @else {
                <app-status-badge [status]="campaign.status" />

                <span class="text-xs">
                  @if (campaignTimeRemainingMap().get(campaign.id) === null) {
                    {{ ('pages.dashboard.campaigns.ended' | translate) + ' ' + (campaign.end | timeago: true) }}
                  } @else {
                    {{
                      'pages.dashboard.campaigns.endsIn'
                        | translate: { timeRemaining: campaignTimeRemainingMap().get(campaign.id) }
                    }}
                  }
                </span>
              }
            </div>

            @if (loadingCampaigns()) {
              <ngx-skeleton-loader class="h-6" [theme]="{ width: '24px', height: '24px', 'border-radius': '100vw' }" />
            } @else {
              <button mat-icon-button class="relative" (click)="openCampaignInNewTab(campaign.id, $event)">
                <div
                  class="border border-shikenso-purple-base-12 top-0 right-0 flex items-center justify-center rounded-full absolute min-w-6 min-h-6"
                >
                  <app-icon src="icons/arrow-up-right.svg" alt="external-link" [size]="16" />
                </div>
              </button>
            }
          </div>

          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-3">
              @if (loadingCampaigns()) {
                <ngx-skeleton-loader class="h-11" [theme]="{ width: '44px', height: '44px', 'border-radius': '4px' }" />
                <ngx-skeleton-loader class="h-4" [theme]="{ width: '80px', height: '16px', 'border-radius': '4px' }" />
              } @else {
                <app-icon-badge
                  backgroundColor="transparent"
                  fallbackBackgroundColor="var(--shikenso-base-white)"
                  [content]="campaign.iconId"
                  [fallbackText]="campaign.name"
                  [size]="44"
                  [iconSize]="44"
                  [fontSize]="20"
                  [borderRadius]="4"
                />

                <span
                  class="text-sm flex-1 line-clamp-2 text-ellipsis"
                  matTooltip
                  matTooltipPosition="above"
                  [appTextClampTooltip]="campaign.name"
                >
                  {{ campaign.name }}
                </span>
              }
            </div>

            <div class="flex items-center">
              <div class="flex flex-col justify-center gap-1 w-[106px]">
                <span class="text-[10px] text-shikenso-purple-base-60">{{ 'shared.customer' | translate }}</span>

                @if (loadingCampaigns()) {
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '16px', height: '16px', 'border-radius': '100vw' }"
                  />
                } @else {
                  @if (campaign.customer && campaign.customer.iconId) {
                    <app-icon-badge
                      backgroundColor="transparent"
                      fallbackBackgroundColor="var(--shikenso-base-white)"
                      [content]="campaign.customer.iconId"
                      [fallbackText]="campaign.customer.name || ''"
                      [size]="16"
                      [iconSize]="16"
                      [fontSize]="10"
                      [borderRadius]="4"
                    />
                  } @else {
                    <span class="leading-4 text-xs text-shikenso-purple-base-60">{{ 'shared.na' | translate }}</span>
                  }
                }
              </div>

              <div class="flex flex-col justify-center gap-1 w-[106px]">
                <span class="text-[10px] text-shikenso-purple-base-60">{{ 'shared.creators' | translate }}</span>

                @if (loadingCampaigns()) {
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '16px', height: '16px', 'border-radius': '100vw' }"
                  />
                } @else {
                  <app-icon-badge-list
                    backgroundColor="transparent"
                    fallbackBackgroundColor="var(--shikenso-base-white)"
                    [iconData]="getCreatorsIconData(campaign)"
                    [borderRadius]="100"
                    [borderWidth]="1"
                    [size]="16"
                    [iconSize]="16"
                    [fontSize]="10"
                    [shownIconsCount]="4"
                  />
                }
              </div>
            </div>

            <div class="flex items-center gap-2">
              <div class="flex flex-col gap-1">
                <span class="text-[10px] text-shikenso-purple-base-60">{{ 'shared.progress' | translate }}</span>

                @if (loadingCampaigns()) {
                  <ngx-skeleton-loader class="flex h-2 self-end" [theme]="{ width: '182px', height: '8px' }" />
                } @else if (loadingCampaignAnalytics()) {
                  <ngx-skeleton-loader
                    class="flex h-2 self-end"
                    [theme]="{ width: `${getProgressBarWidth(campaign.id)}px`, height: '8px' }"
                  />
                } @else {
                  @let progress = getCampaignProgress(campaign);

                  <app-progress-bar
                    class="self-end"
                    [currentColor]="progress.color"
                    [current]="progress.current"
                    [total]="progress.total"
                    [height]="8"
                    [isProgressLabelVisible]="false"
                    [minWidth]="getProgressBarWidth(campaign.id)"
                    [showProgressTooltip]="true"
                    [showPercentageTooltip]="true"
                  />
                }
              </div>

              @if (loadingCampaigns()) {
                <div class="flex items-center self-end">
                  <ngx-skeleton-loader class="h-5" [theme]="{ width: '20px', height: '20px' }" />
                </div>
              } @else {
                <app-social-badge-list
                  [badgeData]="campaignPlatformsMap().get(campaign.id) ?? []"
                  [size]="16"
                  [iconSize]="16"
                  [showTooltip]="true"
                />
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</div>
